// sections.js — nav, section chrome, pricing, FAQ, final CTA, footer (ported from
// website2-sections.jsx). CTAs are wired to the app's real destinations: primary
// CTAs go to /login, "Book a demo" goes to the existing Calendly link.
// The design's mobile burger menu is intentionally skipped (mobile not ready).
import React from 'react'
import { Link } from 'react-router-dom'
import { N2Ic, N2Paw } from './icons'
import { N2Btn, N2Sticker } from './shell'
import { trackBookDemoClick, trackGetStartedClick, trackLoginClick } from './track'
import namiLogo from '../../assets/nami-logo.png'

export const DEMO_URL = 'https://calendly.com/kevin-nami/30min'

export function N2Scallop({ fill = '#FFFFFF', flip = false }) {
	// nami = wave: asymmetric swells cresting left-to-right — long gentle back, steep breaking face
	let main = 'M0 30 L0 17 ', echo = 'M-48 30 L-48 21 '
	for (let i = 0; i < 15; i++) main += 'q 40 -3 60 -13 q 9 11 36 13 '
	for (let i = 0; i < 16; i++) echo += 'q 40 -2 60 -9 q 9 7.5 36 9 '
	main += 'L1440 30 Z'
	echo += 'L1488 30 Z'
	return (
		<svg className="n2-scallop" viewBox="0 0 1440 30" preserveAspectRatio="none" aria-hidden="true"
			style={flip ? { transform: 'scaleY(-1)', marginTop: '-1px', marginBottom: 0 } : null}>
			<path d={echo} fill={fill} opacity="0.38"></path>
			<path d={main} fill={fill}></path>
		</svg>
	)
}

export function N2SectionHead({ eyebrow, title, sub, dark = false }) {
	return (
		<div className={'n2-shead n2-reveal' + (dark ? ' dark' : '')}>
			<span className="n2-eyebrow"><N2Paw size={13} />{eyebrow}</span>
			<h2 className="n2-h2">{title}</h2>
			{sub ? <p className="n2-sub">{sub}</p> : null}
		</div>
	)
}

/* ---------- nav ---------- */
export function N2Nav({ cta }) {
	const [scrolled, setScrolled] = React.useState(false)
	React.useEffect(() => {
		// The app scrolls on <body> (html/body carry overflow-x: hidden + height: 100%
		// in index.css), so listen in the capture phase and read whichever scrolls.
		const top = () => window.scrollY || document.body.scrollTop || document.documentElement.scrollTop || 0
		const fn = () => setScrolled(top() > 24)
		fn()
		window.addEventListener('scroll', fn, { capture: true, passive: true })
		return () => window.removeEventListener('scroll', fn, { capture: true })
	}, [])
	return (
		<nav className={'n2-nav' + (scrolled ? ' is-scrolled' : '')}>
			<div className="n2-nav-in">
				<a className="n2-logo" href="#top"><img src={namiLogo} alt="Nami" /><span>Nami</span></a>
				<div className="n2-nav-links">
					<a href="#top">Product</a>
					<a href="#features">Features</a>
					<a href="#pricing">Pricing</a>
					<a href="#faq">FAQ</a>
				</div>
				<div className="n2-nav-cta">
					<Link className="n2-nav-login" to="/login" onClick={trackLoginClick('nav')}>Log in</Link>
					<N2Btn href={DEMO_URL} target="_blank" rel="noopener noreferrer" ghost small onClick={trackBookDemoClick('nav')}>Book a demo</N2Btn>
					<N2Btn to="/login" small onClick={trackGetStartedClick('nav', cta)}>{cta}</N2Btn>
				</div>
			</div>
		</nav>
	)
}

/* ---------- pricing ---------- */
const N2_PRICE_FEATURES = [
	{ t: 'Unlimited appointments' },
	{ t: 'Smart calendar & scheduling' },
	{ t: 'Online booking page' },
	{ t: 'Two-way client texting' },
	{ t: 'Texts turn into booked appointments', ai: true },
	{ t: 'Automated reminders & confirmations' },
	{ t: 'Client & pet profiles' },
	{ t: 'Square checkout, tips & invoices' },
	{ t: 'Digital agreements' },
	{ t: 'Staff management' },
	{ t: 'Dashboard & reports' }
]
const N2_PRICE_PERKS = [
	{ icon: 'heart', tone: 'blush', t: 'Founding perks, locked for life', s: 'Whatever Nami costs one day, founding shops keep lifetime benefits.' },
	{ icon: 'users', tone: 'lav', t: 'We move you in — free', s: 'Clients, pets, notes, history: we migrate everything from your current software.' },
	{ icon: 'chat', tone: 'mint', t: 'A direct line to the builders', s: 'Fast answers from the people making Nami — and your feedback shapes it.' }
]
export function N2Pricing() {
	return (
		<section className="n2-section" id="pricing">
			<div className="n2-wrap">
				<N2SectionHead eyebrow="Pricing" title={<React.Fragment>Every feature. <em>Zero dollars.</em></React.Fragment>}
					sub="We’re opening Nami to early grooming shops completely free — the whole product, no limits, no card. You help shape it, and keep founding perks for life." />
				<div className="n2-pricewrap n2-reveal">
					<i className="n2-price-halo" aria-hidden="true"></i>
					<div className="n2-price">
						<i className="n2-price-notch top" aria-hidden="true"></i>
						<i className="n2-price-notch bot" aria-hidden="true"></i>
						<div className="n2-price-main">
							<div className="n2-price-badgerow"><N2Sticker blush icon="heart" style={{ position: 'static', display: 'inline-flex' }}>Founding shop offer</N2Sticker></div>
							<p className="n2-price-was">Software like this usually runs <s>$60–$130 / mo</s></p>
							<div className="n2-price-num">
								<strong>$0</strong>
								<div className="n2-price-per"><b>/ month</b><span>every feature · no credit card</span></div>
							</div>
							<svg className="n2-squig" viewBox="0 0 150 12" fill="none" aria-hidden="true">
								<path d="M2 8 Q 11 2, 20 7 T 38 7 T 56 7 T 74 7 T 92 7 T 110 7 T 128 7 T 148 5" stroke="currentColor" strokeWidth="4" strokeLinecap="round"></path>
							</svg>
							<div className="n2-price-perks">
								{N2_PRICE_PERKS.map((p) => (
									<div className="n2-perk" key={p.t}>
										<span className={'n2-perk-ic ' + p.tone}><N2Ic name={p.icon} size={17} sw={2} /></span>
										<div><b>{p.t}</b><span>{p.s}</span></div>
									</div>
								))}
							</div>
							<N2Btn to="/login" className="n2-price-cta" onClick={trackGetStartedClick('pricing', 'Get started free today')}>Get started free today</N2Btn>
						</div>
						<aside className="n2-price-side">
							<span className="n2-price-side-k">Everything included</span>
							<h3 className="n2-price-side-t">The whole product — no tiers, no add-ons.</h3>
							<ul className="n2-price-list">{N2_PRICE_FEATURES.map((f) => (
								<li key={f.t} className={f.ai ? 'is-ai' : ''}>
									<span className={'n2-pl-ic' + (f.ai ? ' ai' : '')}><N2Ic name={f.ai ? 'spark' : 'check'} size={12} sw={f.ai ? 1.8 : 2.8} /></span>
									{f.t}{f.ai ? <em className="n2-pl-ai">AI</em> : null}
								</li>
							))}</ul>
							<div className="n2-price-total"><span>Due today</span><strong>$0</strong></div>
						</aside>
					</div>
				</div>
			</div>
		</section>
	)
}

/* ---------- FAQ ---------- */
const N2_FAQS = [
	{ q: 'Is Nami really free?', a: 'Yes. Full access, no credit card, no trial timer, no locked features.' },
	{ q: 'What’s the catch?', a: 'There isn’t one. We’re looking for early shops to use Nami completely free, with no limits. In exchange, you get lifetime benefits and discounts as we grow. That’s it.' },
	{ q: 'Why choose Nami?', a: 'Premium tools have always been built for big salons and chains, leaving solo groomers and small shops with stripped-down versions of software that wasn’t designed for them in the first place. We think that’s backwards. Nami brings the premium experience to the groomers who need it most.' },
	{ q: 'Can I bring my clients and pets over from my current software?', a: 'Yes. We’ll transfer everything for you, completely free. Email kevin@nami.pet to set it up.' },
	{ q: 'Does Nami work with Square?', a: 'Yes. Keep the payment system you already trust, and let Nami handle scheduling, booking, and client management around it.' },
	{ q: 'Do I need to be tech savvy?', a: 'Not at all. Nami was designed to be simple and intuitive, built for people who run their business, not their software. No learning curve, no training required.' },
	{ q: 'Who helps me when something breaks?', a: 'You get a direct line to the team building Nami. Email kevin@nami.pet for fast, responsive support straight from the people building the product.' }
]
export function N2FAQ() {
	// Multi-open accordion: expanding a question leaves the others as they are.
	const [open, setOpen] = React.useState([0])
	const toggle = (i) => setOpen((o) => o.includes(i) ? o.filter((x) => x !== i) : [...o, i])
	return (
		<section className="n2-section" id="faq" style={{ paddingTop: 0 }}>
			<div className="n2-wrap">
				<N2SectionHead eyebrow="FAQ" title={<React.Fragment>Fair <em>questions.</em></React.Fragment>} />
				<div className="n2-faqs">
					{N2_FAQS.map((f, i) => {
						const on = open.includes(i)
						return (
							<div className={'n2-faq n2-reveal' + (on ? ' is-open' : '')} key={i}>
								<button type="button" className="n2-faq-q" aria-expanded={on} onClick={() => toggle(i)}>
									<span>{f.q}</span><span className="n2-faq-tick"><N2Ic name="plus" size={16} sw={2.2} /></span>
								</button>
								<div className="n2-faq-a"><div>{f.a}</div></div>
							</div>
						)
					})}
				</div>
			</div>
		</section>
	)
}

/* ---------- final CTA + footer ---------- */
export function N2Final({ cta }) {
	return (
		<section className="n2-section" style={{ paddingBottom: 0 }}>
			<div className="n2-final n2-reveal">
				<span className="n2-final-paw" style={{ top: -50, left: -30, transform: 'rotate(-16deg)' }}><N2Paw size={220} /></span>
				<span className="n2-final-paw" style={{ bottom: -70, right: -24, transform: 'rotate(20deg)' }}><N2Paw size={280} /></span>
				<h2 className="n2-h2">Ready when <em>you</em> are.</h2>
				<p className="n2-sub">Join the founding shops running their days on Nami. Set up takes minutes, and your first booking might beat you to it.</p>
				<div className="n2-hero-ctas">
					<N2Btn to="/login" onClick={trackGetStartedClick('final', cta)}>{cta}</N2Btn>
					<N2Btn href={DEMO_URL} target="_blank" rel="noopener noreferrer" ghost onClick={trackBookDemoClick('final')}>Book a demo</N2Btn>
				</div>
			</div>
		</section>
	)
}
export function N2Footer() {
	return (
		<footer className="n2-footer">
			<div className="n2-footer-top">
				<div className="n2-footer-brand">
					<div className="n2-footer-logo"><img src={namiLogo} alt="" /><span>Nami</span></div>
					<p>The calm, cuddly front desk for dog grooming businesses.</p>
				</div>
				<div className="n2-footer-links">
					<div className="n2-footer-col"><em>Product</em><a href="#top">Tour</a><a href="#features">Features</a><a href="#pricing">Pricing</a></div>
					<div className="n2-footer-col"><em>Company</em><a href="#faq">FAQ</a><a href="mailto:kevin@nami.pet">Contact</a><a href={DEMO_URL} target="_blank" rel="noopener noreferrer" onClick={trackBookDemoClick('footer')}>Book a demo</a></div>
				</div>
			</div>
			<div className="n2-footer-bottom">
				<span>© 2026 Nami · Built with <span className="paw"><N2Paw size={13} /></span> for groomers.</span>
				<span className="spacer"></span>
				<Link to="/privacy">Privacy Policy</Link>
				<Link to="/terms">Terms of Service</Link>
			</div>
		</footer>
	)
}
